<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-300 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <input-text
            name="title"
            label="* Title"
            v-model="form.title"
            class="mt-3"
            :error="errors.title ? errors.title[0] : null"
            :maxlength="255"
        />

        <input-rich-text
            name="text"
            label="* Text"
            v-model="form.text"
            :language="form.language"
            class="mt-3"
            :error="errors.text ? errors.text[0] : null"
            :maxlength="60000"
        />

        <p class="mt-3 text-black dark:text-white">Items</p>

        <draggable
            v-model="form.items"
            ghost-class="bg-gray-500"
            handle=".handle"
            @choose="froalaStore.disableFroala = true"
            @unchoose="froalaStore.disableFroala = false"
        >
            <div v-for="(item, index) in form.items"
                :key="`testimonialItem-${item.id || item.uuid}`"
                class="mt-2 max-w-8xl item pl-10">
                <div class="items-center rounded-lg p-10 relative bg-white dark:bg-gray-700">
                    <div class="absolute -top-1 right-1 z-10 text-black dark:text-white">
                        <div class="flex">
                            <div class="handle" title="Drag to reorder items">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 17l-4 4m0 0l-4-4m4 4V3" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                                </svg>
                            </div>
                            <button
                               v-if="form.items?.length > minItems"
                                @click="() => handleDelete(index)">
                                <svg
                                    class="w-4 h-4 mt-60 -ml-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <input-text
                        :name="`item_[${index}][name]`"
                        label="* Name of the operator contact"
                        v-model="item.operator_contract_name"
                        class="mt-3"
                        :error="errors[`items.${index}.operator_contract_name`] ? errors[`items.${index}.operator_contract_name`][0] : null"
                        :maxlength="255"
                    />

                    <input-text
                        :name="`item_[${index}][position]`"
                        label="* Position of the operator contact"
                        v-model="item.operator_contract_position"
                        class="mt-3"
                        :error="errors[`items.${index}.operator_contract_position`] ? errors[`items.${index}.operator_contract_position`][0] : null"
                        :maxlength="255"
                    />

                    <input-rich-text
                        :name="`item_[${index}][text]`"
                        label="* Text"
                        v-model="item.text"
                        :language="form.language"
                        class="mt-3"
                        :error="errors[`items.${index}.text`] ? errors[`items.${index}.text`][0] : null"
                        :maxlength="60000"
                    />

                    <media-reference
                        :name="`item_[${index}][logo]`"
                        v-model="item.logo"
                        class="mt-3"
                        label="* Logo"
                        :media-types="['OperatorLogo', 'PaymentLogo', 'SoftwareLogo', 'LicenseLogo', 'Logo']"
                        :error="errors[`items.${index}.logo_id`] ? errors[`items.${index}.logo_id`][0] : null"
                        @input="updateLogo(index, $event)"
                    />
                </div>
                <hr>
            </div>
            <button slot="footer"
                class="mt-6 text-indigo-600 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-200 pl-10"
                type="button"
                v-if="form.items?.length < maxItems"
                @click="addItem"
            >
                + Add item
            </button>
        </draggable>

        <div class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3">
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Language</p>
            <reference
                create-text="Create new language and link language to this page"
                link-text="Link an existing language"
                :creatable="false"
                :editable="false"
                :error="errors.language_id ? errors.language_id[0] : null"
                v-model="form.language"
                model-data="Site Language"
                :disabled="true"
            />
        </div>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import draggable from "vuedraggable";
import crudForm from "../mixins/crudForm";
import {useFroalaStore} from "../stores/froala";
import {v4 as uuidv4} from "uuid";

export default {
    components: { draggable },
    name: "TestimonialForm",
    mixins: [crudForm],
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                title: this.getFormData("title"),
                text: this.getFormData("text"),
                items: this.getFormData("items"),
            },
            froalaStore: useFroalaStore(),
            maxItems: 8,
            minItems: 2,
        };
    },
    created() {
        if (!this.form.items?.length) {
            let items = [];
            for (let i = 0; i < this.minItems; i++) {
                items.push(this.defaultItem());
            }
            this.form.items = items;
        }
    },
    methods: {
        defaultItem() {
            return {
                uuid: uuidv4(),
                operator_contract_name: null,
                operator_contract_position: null,
                text: null,
                logo: null,
                logo_id: null,
            }
        },
        addItem(e) {
            e.preventDefault();
            this.form['items'].push(this.defaultItem());
        },
        handleDelete: function (indexOfItemToDelete) {
            if (confirm('Are you sure you want to remove this item?')) {
                this.form['items'].splice(indexOfItemToDelete, 1);
            }
        },
        updateLogo: function (index, logo) {
            this.form['items'][index].logo_id = (logo && logo.id) ? logo.id : null;
        },
    },
};
</script>
